import React, { useEffect } from "react";
import { createPortal } from "react-dom";

/**
 * ModalShell — renders children via a React portal to document.body so that
 * `position: fixed` inside the modal is always relative to the viewport
 * (not to any transformed/filtered ancestor). Also:
 *   • locks body scroll while mounted
 *   • reserves the scrollbar width to avoid a layout shift
 *   • closes on Escape
 *   • closes when clicking the backdrop (but not when clicking inside the card)
 *
 * Usage:
 *   <ModalShell onClose={close} testId="branch-editor-modal" maxWidth="md">
 *     <h2>...</h2>
 *     ...
 *   </ModalShell>
 */
export default function ModalShell({
  children,
  onClose,
  testId,
  maxWidth = "md",          // sm | md | lg
  scrollable = true,
}) {
  useEffect(() => {
    const body = document.body;
    const prevOverflow = body.style.overflow;
    const prevPadding = body.style.paddingRight;
    const sbw = window.innerWidth - document.documentElement.clientWidth;
    body.style.overflow = "hidden";
    if (sbw > 0) body.style.paddingRight = `${sbw}px`;

    const onKey = (e) => {
      if (e.key === "Escape" && typeof onClose === "function") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      body.style.overflow = prevOverflow;
      body.style.paddingRight = prevPadding;
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const maxW = { sm: "max-w-sm", md: "max-w-md", lg: "max-w-lg", "2xl": "max-w-2xl", "3xl": "max-w-3xl" }[maxWidth] || "max-w-md";
  const scrollCls = scrollable ? "max-h-[90vh] overflow-y-auto" : "";

  return createPortal(
    <div className="fixed inset-0 bg-black/40 z-[9999] flex items-center justify-center p-4"
         data-testid={testId} onClick={onClose}>
      <div className={`bg-white rounded-2xl w-full ${maxW} p-5 space-y-3 ${scrollCls}`}
           style={{ background: "var(--bg-paper)" }}
           onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>,
    document.body
  );
}
